import { Button } from "@fluentui/react-components";
import React from "react";
import {
  SectionCard,
  FieldRow,
  LabeledInput,
  LabeledSelect,
} from "../forms/formPrimitives";
import { bestaetigen } from "../redesign/bestaetigen";
import {
  documentService,
  type DocumentDto,
} from "../../services/documentService";
import {
  partnerVertragService,
  type PartnerVertragDaten,
  type PartnerVertrag,
} from "../../services/partnerVertragService";
const SPARTEN = [
  ["kfz_flotte", "Kfz/Flotte"],
  ["sach_komposit", "Sach Komposit"],
  ["haftpflicht", "Haftpflicht"],
  ["financial_lines", "Financial Lines"],
  ["unfall", "Unfall"],
  ["transport", "Transport"],
  ["technische_versicherungen", "Technische Versicherungen"],
  ["rechtsschutz", "Rechtsschutz"],
  ["cyber", "Cyber"],
  ["kredit_kaution_buergschaft", "Kredit-Kaution-Bürgschaft"],
  ["leben", "Leben"],
  ["kranken", "Kranken"],
  ["spezialkonzepte", "Spezialkonzepte"],
  ["sonstiges", "Sonstiges"],
] as const;
const KATEGORIEN = [
  ["partner_vertrag", "Vertrag"],
  ["partner_courtagezusage", "Courtagezusage"],
  ["partner_nachtrag", "Nachtrag"],
  ["partner_zeichnungsrichtlinie", "Zeichnungsrichtlinie"],
  ["partner_datenschutzvereinbarung", "Datenschutzvereinbarung"],
  [
    "partner_schadenregulierungsvereinbarung",
    "Schadenregulierungsvereinbarung",
  ],
] as const;
type Karte = PartnerVertragDaten & { key: string; id?: number; stand?: string };
type Datei = { key: string; file: File; category: string; fehler?: string };
const daten = ({
  beginn,
  ende,
  status,
  sparten,
  spartenSonstiges,
  sort,
}: PartnerVertragDaten): PartnerVertragDaten => ({
  beginn,
  ende,
  status,
  sparten,
  spartenSonstiges,
  sort,
});
const signatur = (v: PartnerVertragDaten) => JSON.stringify(daten(v));
const karte = (v: PartnerVertrag): Karte => ({
  ...v,
  key: `db-${v.id}`,
  stand: signatur(v),
});
const fehlermeldung = (e: unknown) =>
  e instanceof Error ? e.message : "Speichern fehlgeschlagen";
export interface PartnerVertragskartenRef {
  geaendert: () => boolean;
  speichern: (contactId: number) => Promise<string[]>;
}
/** Anlage und Akte verwenden dieselben Entwürfe. Erfolgreiche Einzelaktionen werden sofort quittiert. */
export const PartnerVertragskarten = React.forwardRef<
  PartnerVertragskartenRef,
  { contactId?: number; nurLesen?: boolean }
>(function PartnerVertragskarten({ contactId, nurLesen = false }, ref) {
  const [, render] = React.useReducer((n) => n + 1, 0);
  const state = React.useRef({
    karten: [] as Karte[],
    dateien: [] as Datei[],
    dokumente: [] as DocumentDto[],
    entfernt: [] as number[],
    geladen: !contactId,
    fehler: "",
    speichert: false,
  });
  const generation = React.useRef(0);
  const [ladeVersuch, setLadeVersuch] = React.useState(0);
  React.useEffect(() => {
    const g = ++generation.current;
    const abort = new AbortController();
    state.current = {
      karten: [],
      dateien: [],
      dokumente: [],
      entfernt: [],
      geladen: !contactId,
      fehler: "",
      speichert: false,
    };
    render();
    if (contactId)
      void Promise.all([
        partnerVertragService.list(contactId, abort.signal),
        ...KATEGORIEN.map(([category]) =>
          documentService.list({ contactId, category, limit: 1000 }),
        ),
      ])
        .then(([vertraege, ...listen]) => {
          if (g !== generation.current || abort.signal.aborted) return;
          state.current.karten = (vertraege as PartnerVertrag[]).map(karte);
          state.current.dokumente = (listen as DocumentDto[][]).flat();
          state.current.geladen = true;
          render();
        })
        .catch((e) => {
          if (g !== generation.current || abort.signal.aborted) return;
          state.current.fehler = fehlermeldung(e);
          render();
        });
    return () => {
      abort.abort();
      generation.current++;
    };
  }, [contactId, ladeVersuch]);
  const s = state.current;
  const dirty = () => {
    const x = state.current;
    return (
      x.dateien.length > 0 ||
      x.entfernt.length > 0 ||
      x.karten.some((k) => !k.id || signatur(k) !== k.stand)
    );
  };
  React.useImperativeHandle(
    ref,
    () => ({
      geaendert: dirty,
      speichern: async (id) => {
        const x = state.current;
        const g = generation.current;
        if (nurLesen) return [];
        if (!x.geladen)
          return [
            "Partnerverträge und Dokumente sind noch nicht vollständig geladen.",
          ];
        if (x.speichert) return ["Partnerdaten werden bereits gespeichert."];
        x.speichert = true;
        x.fehler = "";
        render();
        const probleme: string[] = [];
        try {
          for (const k of x.karten) {
            if (g !== generation.current)
              return ["Kontakt wurde während des Speicherns gewechselt."];
            if (k.id && signatur(k) === k.stand) continue;
            try {
              const result = k.id
                ? await partnerVertragService.update(id, k.id, daten(k))
                : await partnerVertragService.create(id, daten(k));
              k.id = result.id;
              k.stand = signatur(k);
            } catch (e) {
              probleme.push(
                `Vertrag ${x.karten.indexOf(k) + 1}: ${fehlermeldung(e)}`,
              );
            }
          }
          for (const vertragId of [...x.entfernt]) {
            if (g !== generation.current)
              return ["Kontakt wurde während des Speicherns gewechselt."];
            try {
              await partnerVertragService.remove(id, vertragId);
              x.entfernt = x.entfernt.filter((v) => v !== vertragId);
            } catch (e) {
              probleme.push(`Vertrag entfernen: ${fehlermeldung(e)}`);
            }
          }
          for (const d of [...x.dateien]) {
            if (g !== generation.current)
              return ["Kontakt wurde während des Speicherns gewechselt."];
            try {
              const result = await documentService.upload({
                file: d.file,
                category: d.category,
                contactId: id,
              });
              x.dokumente.push({ ...result, category: d.category });
              x.dateien = x.dateien.filter((v) => v.key !== d.key);
            } catch (e) {
              d.fehler = fehlermeldung(e);
              probleme.push(`${d.file.name}: ${d.fehler}`);
            }
          }
          x.fehler = probleme.join("\n");
          return probleme;
        } finally {
          x.speichert = false;
          if (g === generation.current) render();
        }
      },
    }),
    [nurLesen],
  );
  const aendern = (k: Karte, patch: Partial<PartnerVertragDaten>) => {
    if (nurLesen || state.current.speichert) return;
    Object.assign(k, patch);
    render();
  };
  const entfernen = async (k: Karte) => {
    if (nurLesen || s.speichert) return;
    if (
      !(await bestaetigen({
        titel: "Vertrag entfernen?",
        text: "Der Vertrag wird beim Speichern in den Papierkorb gelegt.",
        ton: "danger",
      }))
    )
      return;
    if (k.id) s.entfernt.push(k.id);
    s.karten = s.karten.filter((v) => v.key !== k.key);
    render();
  };
  const herunterladen = async (d: DocumentDto) => {
    try {
      const blob = await documentService.fetchBlob(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      s.fehler = fehlermeldung(e);
      render();
    }
  };
  return (
    <>
      <SectionCard title="Vertragsdaten">
        {s.fehler && (
          <div
            role="alert"
            style={{ whiteSpace: "pre-line", color: "var(--danger)" }}
          >
            {s.fehler}
          </div>
        )}
        {!s.geladen && (
          <>
            <p>Partnerdaten konnten noch nicht geladen werden.</p>
            {s.fehler && (
              <Button
                type="button"
                onClick={() => setLadeVersuch((v) => v + 1)}
              >
                Erneut laden
              </Button>
            )}
          </>
        )}
        <fieldset
          disabled={nurLesen || s.speichert || !s.geladen}
          style={{ border: 0, padding: 0, minWidth: 0 }}
        >
          {s.karten.map((k, i) => (
            <SectionCard key={k.key} title={`Vertrag ${i + 1}`}>
              <FieldRow columns={2}>
                <LabeledInput
                  label="Vertragsbeginn"
                  name={`beginn-${k.key}`}
                  type="date"
                  value={k.beginn ?? ""}
                  onChange={(beginn) => aendern(k, { beginn: beginn || null })}
                />
                <LabeledInput
                  label="Vertragsende"
                  name={`ende-${k.key}`}
                  type="date"
                  value={k.ende ?? ""}
                  onChange={(ende) => aendern(k, { ende: ende || null })}
                />
              </FieldRow>
              <LabeledSelect
                label="Vertragsstatus"
                name={`status-${k.key}`}
                value={k.status}
                onChange={(status) => aendern(k, { status })}
                options={["aktiv", "in Prüfung", "ruhend", "gekündigt"].map(
                  (v) => ({ value: v, label: v }),
                )}
              />
              <fieldset style={{ border: 0, padding: 0 }}>
                <legend>Sparten</legend>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))",
                    gap: 8,
                  }}
                >
                  {SPARTEN.map(([value, label]) => (
                    <label key={value}>
                      <input
                        type="checkbox"
                        checked={k.sparten.includes(value)}
                        onChange={(e) =>
                          aendern(k, {
                            sparten: e.target.checked
                              ? [...k.sparten, value]
                              : k.sparten.filter((v) => v !== value),
                          })
                        }
                      />{" "}
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
              {k.sparten.includes("sonstiges") && (
                <LabeledInput
                  label="Sonstige Sparte"
                  name={`sonstiges-${k.key}`}
                  value={k.spartenSonstiges}
                  maxLength={2000}
                  onChange={(spartenSonstiges) =>
                    aendern(k, { spartenSonstiges })
                  }
                />
              )}
              <Button type="button" onClick={() => void entfernen(k)}>
                Vertrag entfernen
              </Button>
            </SectionCard>
          ))}
          <Button
            type="button"
            onClick={() => {
              s.karten.push({
                key: crypto.randomUUID(),
                beginn: null,
                ende: null,
                status: "aktiv",
                sparten: [],
                spartenSonstiges: "",
                sort: s.karten.length,
              });
              render();
            }}
          >
            Weiteren Vertrag hinzufügen
          </Button>
        </fieldset>
      </SectionCard>
      <SectionCard title="Dokumente">
        {KATEGORIEN.map(([category, label]) => (
          <div
            key={category}
            style={{
              padding: "12px 0",
              borderBottom: "1px solid var(--line,#ddd)",
            }}
          >
            <strong>{label}</strong>
            <ul>
              {s.dokumente
                .filter((d) => d.category === category)
                .map((d) => (
                  <li key={d.id}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        void herunterladen(d);
                      }}
                    >
                      {d.fileName} herunterladen
                    </a>
                  </li>
                ))}
              {s.dateien
                .filter((d) => d.category === category)
                .map((d) => (
                  <li key={d.key}>
                    {d.file.name} —{" "}
                    {d.fehler
                      ? `Fehler: ${d.fehler} (beim Speichern erneut versuchen)`
                      : "vorgemerkt"}{" "}
                    <Button
                      type="button"
                      disabled={nurLesen || s.speichert}
                      onClick={() => {
                        s.dateien = s.dateien.filter((v) => v.key !== d.key);
                        render();
                      }}
                    >
                      Datei entfernen
                    </Button>
                  </li>
                ))}
            </ul>
            <label>
              {label} hochladen{" "}
              <input
                type="file"
                multiple
                aria-label={`${label} hochladen`}
                disabled={nurLesen || s.speichert || !s.geladen}
                onChange={(e) => {
                  for (const file of Array.from(e.target.files ?? []))
                    s.dateien.push({
                      key: crypto.randomUUID(),
                      file,
                      category,
                    });
                  e.target.value = "";
                  render();
                }}
              />
            </label>
          </div>
        ))}
      </SectionCard>
    </>
  );
});
